/**
 * Creative ids of AI Video generations this browser has submitted and not yet seen reach a
 * terminal state.
 *
 * The backend pushes UpdateAiVideoStatus exactly once, unbuffered, from the same terminal-once
 * guard that writes the outcome (AiVideoGenerationService.PushCreationPhase). A tab that is
 * mid-refresh, or has not finished JoinBusinessGroup, when that push fires misses it for good.
 * AppComponent reconciles this list against GetMyCreations every time the hub (re)connects, so a
 * completion that landed while the tab was away still raises its toast.
 *
 * localStorage, keyed per browser: a generation started in one tab should still surface in
 * another, and it has to survive the very reload this exists to cover.
 */
export interface PendingAiVideoGeneration {
  creativeId: number;
  /** Trimmed template name — the toast status line, so a reconciled toast reads the same. */
  title: string;
  /** Template preview, played in the toast thumbnail while generating. Absent for image templates. */
  previewVideoUrl?: string;
  /** Epoch ms. Entries older than MAX_AGE_MS are dropped unmatched — the generation is long gone. */
  submittedAt: number;
}

const STORAGE_KEY = 'aiVideoPendingGenerations';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

function read(): PendingAiVideoGeneration[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as PendingAiVideoGeneration[];
    if (!Array.isArray(parsed)) return [];
    const now = Date.now();
    return parsed.filter(
      (entry) =>
        !!entry &&
        typeof entry.creativeId === 'number' &&
        typeof entry.submittedAt === 'number' &&
        now - entry.submittedAt < MAX_AGE_MS,
    );
  } catch {
    return [];
  }
}

function write(entries: PendingAiVideoGeneration[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Private mode or a full quota just means no cross-refresh reconcile — the live push still
    // works. Nothing here is worth failing a submit over.
  }
}

export const AiVideoPendingStore = {
  read,

  /** Record a just-submitted generation. Replaces any existing entry for the same creative. */
  add(entry: Omit<PendingAiVideoGeneration, 'submittedAt'>): void {
    const entries = read().filter((existing) => existing.creativeId !== entry.creativeId);
    entries.push({ ...entry, submittedAt: Date.now() });
    write(entries);
  },

  /** Drop an entry once its outcome has been shown — via the live push or the reconcile. */
  remove(creativeId: number): void {
    const entries = read();
    const next = entries.filter((entry) => entry.creativeId !== creativeId);
    if (next.length !== entries.length) write(next);
  },
};
